import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { PdfViewerCanvas } from '../pdf/PdfViewerCanvas';
import { PdfViewerToolbar } from '../pdf/PdfViewerToolbar';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const INITIAL_SCALE = 1.0;  // 100%
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.2;

export function DocumentPdfViewerModal({ documentId, title, onClose }) {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(INITIAL_SCALE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pdfUrl = documentId ? `/api/documents/raw/${documentId}` : null;

  const handleDocumentLoadSuccess = useCallback(({ numPages: pages }) => {
    setNumPages(pages);
    setPageNumber(1);
    setLoading(false);
    setError(null);
  }, []);

  const handleDocumentLoadError = useCallback(() => {
    setError('Erreur lors du chargement du PDF');
    setLoading(false);
  }, []);

  const handlePrevPage = useCallback(() => {
    setPageNumber((current) => Math.max(current - 1, 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setPageNumber((current) => Math.min(current + 1, numPages || 1));
  }, [numPages]);

  const handleZoomIn = useCallback(() => {
    setScale((current) => Math.min(current + SCALE_STEP, MAX_SCALE));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((current) => Math.max(current - SCALE_STEP, MIN_SCALE));
  }, []);

  useEffect(() => {
    function handleKey(event) {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        handlePrevPage();
      } else if (event.key === 'ArrowRight') {
        handleNextPage();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleNextPage, handlePrevPage, onClose]);

  if (!documentId) {
    return null;
  }

  return (
    <div
      data-testid="document-pdf-viewer-overlay"
      className="fixed inset-0 z-50 flex flex-col bg-black/95"
      onClick={onClose}
    >
      <div
        className="flex h-full flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative">
          <PdfViewerToolbar
            documentTitle={title || 'Document'}
            scale={scale}
            pageNumber={pageNumber}
            numPages={numPages}
            pdfUrl={pdfUrl}
            onZoomOut={handleZoomOut}
            onZoomIn={handleZoomIn}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
          />
          <button
            type="button"
            data-testid="close-document-pdf-viewer"
            onClick={onClose}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
            title="Fermer (Échap)"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <PdfViewerCanvas
          containerRef={containerRef}
          loading={loading}
          error={error}
          pdfUrl={pdfUrl}
          pageNumber={pageNumber}
          scale={scale}
          onDocumentLoadSuccess={handleDocumentLoadSuccess}
          onDocumentLoadError={handleDocumentLoadError}
        />
      </div>
    </div>
  );
}
